import React, { useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { components } from 'react-select';
import { setElementsTemplateSettings, useElementsTemplateSettings } from 'reducers/reviewCreationSlice';

import { OutlineEyeSmall, PlusNotOutlined, Template } from 'assets/assetsV1/iconComponents';

import { getReviewSettingsTemplateRoute } from 'constants/helper';
import { LogoWithoutNameImage } from 'constants/icons';
import { routes } from 'constants/routes';

import { Button } from 'uiToolkitV1/Button';
import {
  ExpandablePaper,
  ExpandablePaperBody,
  ExpandablePaperHeader,
} from 'uiToolkitV1/ExpandablePaper/ExpandablePaper';
import { SelectDashed } from 'uiToolkitV1/Select';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { useGetTemplateList } from '../../Hooks/dataHooks/elementsDataHooks';

const REVIEWER_LABELS = {
  SELF: 'Self Review',
  PEER: 'Peer Review',
  MANAGER: 'Manager Review',
  UPWARD: 'Upward Review',
  DLM: 'Dotted Line Manager Review',
};

export const Option = (props) => {
  const { Option } = components;
  const { data } = props;
  return (
    <Option {...props}>
      <Flex sx={{ color: '#333' }}>
        {data.label}
        {data.templateData.isSample && (
          <img src={LogoWithoutNameImage} alt="Logo" style={{ width: '18px', height: '18px', marginLeft: '12px' }} />
        )}
      </Flex>
    </Option>
  );
};

export const SingleValue = (props) => {
  const { SingleValue } = components;
  const { data } = props;
  return (
    <SingleValue {...props}>
      <Flex sx={{ color: '#333' }}>
        {data.label}
        {data.templateData.isSample && (
          <img src={LogoWithoutNameImage} alt="Logo" style={{ width: '18px', height: '18px', marginLeft: '12px' }} />
        )}
      </Flex>
    </SingleValue>
  );
};

export const TemplateSelection = ({ feedbackFormId }) => {
  const dispatch = useDispatch();
  const { data: templates = [], isLoading } = useGetTemplateList();
  const templateMap = useElementsTemplateSettings({ feedbackFormId });

  const onChangeHandler = ({ data, providerType }) => {
    dispatch(
      setElementsTemplateSettings({
        feedbackFormId,
        settings: { providerType, template: data.templateData },
      })
    );
  };

  const createTemplateButton = (
    <Button
      StartIcon={PlusNotOutlined}
      variant="outlined"
      p="0 6px 0 0"
      height="32px"
      iconSize="24px"
      mr="3"
      as="a"
      target="_blank"
      href={`${getReviewSettingsTemplateRoute()}&isCreateTemplatePanelOpen=true`}
    >
      Create Template
    </Button>
  );

  const templateSelectOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: template.name,
        value: template.id,
        templateData: template,
      })),
    [templates]
  );

  return (
    <ExpandablePaper>
      <ExpandablePaperHeader
        title="Assign reviewer-wise templates"
        leftIconComponent={<Template boxSize="24px" color="icon.g2" />}
        rightComponent={createTemplateButton}
      />
      <ExpandablePaperBody>
        <Flex gap={3} flexDir="column" p="10px 8px 10px 8px" maxW="650px">
          <TextBodyMedium mb={4}>
            Select templates for each type of reviewer from the list of pre-created templates or create a new one
          </TextBodyMedium>

          <Flex gap={3} flexDir="column" maxW="500px">
            {templateMap.map(({ template, providerType }) => (
              <Flex alignItems="center" mb={4}>
                <TextLabel minW="120px">{REVIEWER_LABELS[providerType]}:</TextLabel>
                <SelectDashed
                  value={template ? templateSelectOptions.filter((o) => o.value === template.id)[0] : null}
                  placeholder="Select a template"
                  options={templateSelectOptions}
                  handleChange={(data) => {
                    onChangeHandler({ data, providerType });
                  }}
                  minWidth="380px"
                  isDisabled={isLoading}
                  components={{ Option, SingleValue }}
                />
                {template && (
                  <Flex>
                    <Box borderRight="1px solid #EBEFF3" ml={5} />
                    <Button
                      variant="tertiary"
                      ml={5}
                      as="a"
                      target="_blank"
                      href={routes.template.review.preview.replace(':templateId', template?.id)}
                      StartIcon={OutlineEyeSmall}
                    >
                      Preview this Template
                    </Button>
                  </Flex>
                )}
              </Flex>
            ))}
          </Flex>
        </Flex>
      </ExpandablePaperBody>
    </ExpandablePaper>
  );
};
